<template>
  <v-main>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <v-row justify="center" class="fill-height">
        <v-col cols="12">
          <!-- SoC Mapping Hero Card -->
          <v-card class="mb-6 sb-hero-card" elevation="4">
            <div class="sb-hero-header">
              <div class="sb-hero-header-content">
                <div class="d-flex align-center mb-2">
                  <v-avatar size="48" class="mr-3 sb-hero-logo">
                    <v-icon size="28" color="white">mdi-link-variant</v-icon>
                  </v-avatar>
                  <div>
                    <h1 class="sb-hero-title">Security Assurance Case Builder</h1>
                    <p class="sb-hero-subtitle">
                      Map your Security Assurance Case to a Subject of Certification
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <v-card-text class="pt-6">
              <p class="text-body-1 mb-5 sb-intro-text">
                Create a <strong>Security Assurance Case</strong> by mapping it to an existing
                Subject of Certification (SoC). This establishes a complete compliance assessment
                linking security requirements to your specific system.
              </p>

              <!-- Info Cards -->
              <div class="sb-workflow-section">
                <div class="sb-section-header mb-4">
                  <v-icon color="primary" size="28" class="mr-2">mdi-information-outline</v-icon>
                  <span class="text-h6 font-weight-bold">How It Works</span>
                </div>

                <v-row>
                  <v-col cols="12" md="6">
                    <div class="sb-info-card">
                      <div class="sb-info-icon-wrapper map-icon">
                        <v-icon size="32" color="white">mdi-map-marker-path</v-icon>
                      </div>
                      <div class="sb-info-title">Why Map to a Subject of Certification?</div>
                      <ul class="sb-info-list">
                        <li>
                          <v-icon x-small color="primary" class="mr-1">mdi-check-circle</v-icon>
                          Provides context for security requirements
                        </li>
                        <li>
                          <v-icon x-small color="primary" class="mr-1">mdi-check-circle</v-icon>
                          Links assurance evidence to a specific system
                        </li>
                        <li>
                          <v-icon x-small color="primary" class="mr-1">mdi-check-circle</v-icon>
                          Establishes traceability for compliance
                        </li>
                        <li>
                          <v-icon x-small color="primary" class="mr-1">mdi-check-circle</v-icon>
                          Enables assessment against relevant standards
                        </li>
                      </ul>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="sb-info-card">
                      <div class="sb-info-icon-wrapper setup-icon">
                        <v-icon size="32" color="white">mdi-checkbox-marked-circle-outline</v-icon>
                      </div>
                      <div class="sb-info-title">How to Complete Setup</div>
                      <ul class="sb-info-list">
                        <li>
                          <v-icon x-small color="success" class="mr-1">mdi-numeric-1-circle</v-icon>
                          Select a Subject of Certification below
                        </li>
                        <li>
                          <v-icon x-small color="success" class="mr-1">mdi-numeric-2-circle</v-icon>
                          Click the save icon to create your Security Assurance Case
                        </li>
                        <li>
                          <v-icon x-small color="success" class="mr-1">mdi-numeric-3-circle</v-icon>
                          Fill in the required information
                        </li>
                        <li>
                          <v-icon x-small color="success" class="mr-1">mdi-numeric-4-circle</v-icon>
                          View and manage requirements
                        </li>
                      </ul>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>

            <v-alert type="info" outlined class="mx-6 mb-4" dense>
              <v-icon class="mr-2">mdi-information</v-icon>
              <strong>Note:</strong> A Subject of Certification can only be used for one Security
              Assurance Case. Subjects already in use will be marked as unavailable.
            </v-alert>
          </v-card>

          <v-card class="sb-form-card" elevation="3">
            <div class="sb-form-header">
              <v-icon color="white" class="mr-2">mdi-cog-outline</v-icon>
              <span class="white--text text-h6 font-weight-bold"
                >Security Assurance Case Configuration</span
              >
            </div>
            <loader :loading="loading"></loader>

            <div class="sb-config-body">
              <!-- Step label -->
              <div v-if="!isPreview" class="sb-config-step-label">
                <v-icon size="18" color="#1a237e" class="mr-1">mdi-numeric-1-circle</v-icon>
                <span
                  >Select a Subject of Certification to link to this Security Assurance Case</span
                >
              </div>

              <!-- Autocomplete + action row -->
              <div class="sb-config-row">
                <div class="sb-config-select-wrap">
                  <v-autocomplete
                    v-model="socVersionId"
                    :clearable="!isPreview"
                    chips
                    :label="$t('sac.selectSoc')"
                    :items="socList"
                    item-text="name"
                    item-value="currentVersionId"
                    :readonly="isPreview"
                    outlined
                    dense
                    color="#1a237e"
                    item-color="primary"
                    @change="handleSocChange"
                    :item-disabled="item => item.disabled"
                    :menu-props="{ maxHeight: 340, offsetY: true, contentClass: 'sb-soc-menu' }"
                    hide-details
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip
                        :key="item.id"
                        :color="item.disabled ? '#eeeeee' : '#e3f2fd'"
                        :text-color="item.disabled ? '#9e9e9e' : '#1565c0'"
                        class="font-weight-medium"
                        small
                      >
                        <v-icon small left>{{ getSocTypeIcon(item.type) }}</v-icon>
                        {{ item.name }}
                        <span class="text-caption ml-1 font-italic grey--text"
                          >({{ item.standardFamily }})</span
                        >
                      </v-chip>
                    </template>
                    <template v-slot:item="{ item }">
                      <div class="sb-dd-item" :class="{ 'sb-dd-item--disabled': item.disabled }">
                        <v-icon
                          size="15"
                          :color="item.disabled ? '#bdbdbd' : '#1a237e'"
                          class="sb-dd-icon"
                          >{{ getSocTypeIcon(item.type) }}</v-icon
                        >
                        <span class="sb-dd-name">{{ item.name }}</span>
                        <v-chip
                          x-small
                          label
                          :color="item.disabled ? '#f5f5f5' : '#e8eaf6'"
                          :text-color="item.disabled ? '#9e9e9e' : '#3949ab'"
                          class="sb-dd-family"
                          >{{ item.standardFamily }}</v-chip
                        >
                        <span class="sb-dd-status">
                          <v-icon x-small :color="item.disabled ? 'error' : 'success'">{{
                            item.disabled ? 'mdi-lock-outline' : 'mdi-check-circle-outline'
                          }}</v-icon>
                          {{ item.disabled ? 'In use' : 'Free' }}
                        </span>
                      </div>
                    </template>
                  </v-autocomplete>
                </div>

                <!-- Action buttons -->
                <div class="sb-config-actions">
                  <template v-if="socVersionId && !isPreview && editClicked">
                    <v-btn
                      color="#1a237e"
                      dark
                      depressed
                      class="sb-action-btn"
                      @click="enableCreateDialog()"
                    >
                      <v-icon left small>mdi-content-save-outline</v-icon>
                      Save Security Assurance Case
                    </v-btn>
                    <v-btn
                      color="#546e7a"
                      outlined
                      depressed
                      class="sb-action-btn"
                      @click="handleCancel()"
                    >
                      <v-icon left small>mdi-close</v-icon>
                      Cancel
                    </v-btn>
                  </template>
                  <v-btn
                    v-else-if="socVersionId && !isPreview"
                    color="#1a237e"
                    dark
                    depressed
                    class="sb-action-btn"
                    @click="enableCreateDialog()"
                  >
                    <v-icon left small>mdi-content-save-outline</v-icon>
                    Save Security Assurance Case
                  </v-btn>
                  <v-btn
                    v-else-if="isPreview"
                    color="#37474f"
                    dark
                    depressed
                    class="sb-action-btn"
                    @click="handleEditClicked()"
                  >
                    <v-icon left small>mdi-square-edit-outline</v-icon>
                    Edit
                  </v-btn>
                  <div v-else class="sb-action-placeholder">
                    <v-icon color="grey lighten-2" size="20"
                      >mdi-cursor-default-click-outline</v-icon
                    >
                    <span class="sb-action-hint"
                      >Select a Subject of Certification to continue</span
                    >
                  </div>
                </div>
              </div>

              <!-- Status strip -->
              <div v-if="socVersionId" class="sb-status-strip">
                <v-icon
                  size="16"
                  :color="isPreview || isCreated ? 'success' : 'warning'"
                  class="mr-1"
                >
                  {{ isPreview || isCreated ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                </v-icon>
                <span
                  :class="isPreview || isCreated ? 'success--text' : 'warning--text'"
                  class="text-caption font-weight-medium"
                >
                  {{
                    isPreview || isCreated
                      ? 'Security Assurance Case saved'
                      : 'Pending — click Save Security Assurance Case to confirm'
                  }}
                </span>
              </div>
            </div>
            <v-row
              v-if="(isCreated || isPreview) && socVersionId && getSocId(socVersionId)"
              class="px-2 pt-4"
              no-gutters
              justify="center"
            >
              <RequirementsList
                :key="this.socVersionId"
                :socId="getSocId(socVersionId)"
                :socData="getSelectedSoc(socVersionId)"
              />
            </v-row>
            <!-- Show message when SOC is selected but SAC not yet created (only in create mode) -->
            <v-row
              v-else-if="socVersionId && !isCreated && !isPreview"
              class="px-2 pt-4"
              justify="center"
            >
              <v-col cols="12" class="text-center">
                <v-alert type="info" outlined class="mx-auto" style="max-width: 500px;">
                  Please save your Security Assurance Case first to view and manage requirements
                </v-alert>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-dialog v-model="createClicked" max-width="500">
        <v-card>
          <v-card-title>
            {{ $t('sac.createSacTitle') }}
          </v-card-title>
          <v-card-text>
            <v-form ref="createForm" v-model="createFormValid">
              <v-text-field
                v-model="sac.name"
                :label="$t('sac.headers.name')"
                outlined
                dense
                :rules="[rules.required, rules.duplicateName]"
                :error-messages="
                  duplicateNameExists ? 'Security Assurance Case with this name already exists' : ''
                "
                required
                @input="debouncedCheckDuplicate"
              ></v-text-field>
              <v-textarea
                v-model="sac.description"
                :hint="$t('sac.sacDescriptionHint')"
                :label="$t('sac.headers.description')"
                auto-grow
                rows="2"
                max-rows="6"
                outlined
                dense
                :rules="[rules.required]"
                required
              ></v-textarea>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn v-if="!editClicked" color="gray" text @click="cancelCreate">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn v-else color="gray" text @click="handleCancel()">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              v-if="!editClicked && !isPreview"
              :disabled="!createFormValid || !sac.name || !sac.description || duplicateNameExists"
              color="green"
              text
              @click="createSac()"
            >
              {{ editClicked ? $t('update') : $t('sac.createClaim') }}
            </v-btn>
            <v-btn
              v-else
              :disabled="!createFormValid || !sac.name || !sac.description || duplicateNameExists"
              color="green"
              text
              @click="editSac()"
            >
              {{ editClicked ? $t('update') : $t('sac.createClaim') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import Loader from '@/components/helpers/Loader';
import util from '@/mixins/util.js';
import RequirementsList from './RequirementsList.vue';
import Soc from '@/modules/soc/api/soc.js';
import securityAssuranceCaseApi from '../api/securityAssuranceCaseApi';
import EventBus from '@/helpers/event-bus.js';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import { debounce } from 'lodash';

export default {
  name: 'SacBuilder',
  mixins: [util],
  components: {
    Loader,
    RequirementsList,
    Breadcrumbs,
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchSocs();
    this.socVersionId = JSON.parse(localStorage.getItem('socVersionId'));
    this.isPreview = this.socVersionId !== null ? true : false;
    this.$emit('loading', false);
    this.loading = false;
    if (localStorage.getItem('sac')) {
      this.sac = JSON.parse(localStorage.getItem('sac'));
    }

    // Set up debounced function
    this.debouncedCheckDuplicate = debounce(this.checkDuplicateName, 500);
  },
  data() {
    return {
      isPreview: false,
      loading: false,
      socVersionId: null,
      editClicked: false,
      requirementsList: [],
      socList: [],
      isCreated: false,
      sac: {
        name: '',
        description: '',
        version: '',
        socVersionId: '',
      },
      createClicked: false,
      duplicateNameExists: false,
      checkingDuplicate: false,
      createFormValid: false,
      rules: {
        required: value => !!value || 'Required.',
        duplicateName: () =>
          !this.duplicateNameExists || 'Security Assurance Case with this name already exists.',
      },
    };
  },
  methods: {
    getSocId(socVersionId) {
      const soc = this.socList.find(item => item.currentVersionId === socVersionId);
      return soc ? soc.id : null;
    },
    getSelectedSoc(socVersionId) {
      return this.socList.find(item => item.currentVersionId === socVersionId) || null;
    },
    async fetchSocs() {
      this.loading = true;
      try {
        const response = await Soc.all();
        if (response && response.data) {
          // For each SOC, check if it's used in any SAC
          const socsWithUsageInfo = await Promise.all(
            response.data.map(async soc => {
              try {
                const sacResponse = await securityAssuranceCaseApi.getBySocVersionId(
                  soc.currentVersionId,
                );
                const isUsed = sacResponse.data && sacResponse.data.length > 0;
                return {
                  ...soc,
                  disabled: isUsed,
                  usageInfo: isUsed
                    ? {
                        sacCount: sacResponse.data.length,
                        sacNames: sacResponse.data.map(sac => sac.name).join(', '),
                      }
                    : null,
                };
              } catch (err) {
                console.error(`Error checking SAC usage for SOC ${soc.id}:`, err);
                return { ...soc, disabled: false };
              }
            }),
          );
          this.socList = socsWithUsageInfo;
        } else {
          console.error('Invalid response from Soc.all()');
          EventBus.$emit('snackbar', {
            text: this.$t('errorlist.something-went-wrong'),
            color: 'error',
          });
        }
      } catch (error) {
        console.error('Error fetching socs:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    handleSocChange(id) {
      this.socVersionId = id;
      this.isCreated = false; // Reset created state when changing SOC
      this.resetSac(); // Reset the form
    },
    enableCreateDialog() {
      this.createClicked = true;
      this.duplicateNameExists = false;

      this.$nextTick(() => {
        if (this.$refs.createForm) {
          this.$refs.createForm.resetValidation();
        }
      });
    },
    async createSac() {
      // Double-check for duplicates before proceeding
      if (this.duplicateNameExists) {
        EventBus.$emit('snackbar', {
          text: 'Security Assurance Case with this name already exists',
          color: 'error',
        });
        return;
      }

      this.loading = true;
      this.sac.socVersionId = this.socVersionId;
      this.sac.version = 'v1.0';
      try {
        const response = await securityAssuranceCaseApi.create(this.sac);
        if (response.status === 201) {
          this.isCreated = true;
        }
        EventBus.$emit('snackbar', {
          text: this.$t('create.successSac'),
          color: 'green',
        });
      } catch (error) {
        console.error('Error occurred during creation:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.createClicked = false;
        this.loading = false;
      }
    },
    resetSac() {
      this.sac.name = '';
      this.sac.description = '';
      this.sac.version;
      this.sac.socVersionId = '';
      this.duplicateNameExists = false;

      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
    },
    cancelCreate() {
      this.createClicked = false;
      this.resetSac();
    },
    handleEditClicked() {
      this.editClicked = true;
      this.isPreview = false;
    },
    handleCancel() {
      this.sac = JSON.parse(localStorage.getItem('sac'));
      this.createClicked = false;
      this.editClicked = false;
      this.isPreview = true;
    },
    async editSac() {
      // Check for duplicates when renaming
      if (this.editClicked) {
        const originalSac = JSON.parse(localStorage.getItem('sac'));
        if (originalSac && originalSac.name !== this.sac.name) {
          try {
            const response = await securityAssuranceCaseApi.checkDuplicateName(this.sac.name);
            if (response.data) {
              EventBus.$emit('snackbar', {
                text: 'Security Assurance Case with this name already exists',
                color: 'error',
              });
              return;
            }
          } catch (error) {
            console.error('Error checking duplicate name:', error);
          }
        }
      }

      this.loading = true;
      this.sac.socVersionId = this.socVersionId;
      // const [major, minor] = this.sac.version
      //   .slice(1)
      //   .split('.')
      //   .map(Number);
      // const newMajor = major + 1;
      // this.sac.version = `v${newMajor}.${minor}`;
      try {
        await securityAssuranceCaseApi.update(this.sac);
        EventBus.$emit('snackbar', {
          text: this.$t('editSacSuccess'),
          color: 'green',
        });
      } catch (error) {
        console.error('Error occurred during update:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.editClicked = false;
        this.isPreview = true;
        this.createClicked = false;
        this.loading = false;
      }
    },
    getSocTypeIcon(type) {
      switch (type) {
        case 'Organization Processes':
          return 'mdi-cogs';
        case 'Technical Components':
          return 'mdi-devices';
        case 'Service Provider':
          return 'mdi-account-network';
        case 'Cyber Resilience Act':
          return 'mdi-shield-account';
        default:
          return 'mdi-certificate';
      }
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    checkDuplicateName() {
      if (!this.sac.name || this.sac.name.trim() === '') {
        this.duplicateNameExists = false;
        return;
      }

      if (this.editClicked && this.sac.id) {
        const originalSac = JSON.parse(localStorage.getItem('sac'));
        if (originalSac && originalSac.name === this.sac.name) {
          this.duplicateNameExists = false;
          return;
        }
      }

      this.checkingDuplicate = true;
      securityAssuranceCaseApi
        .checkDuplicateName(this.sac.name)
        .then(response => {
          this.duplicateNameExists = response.data;

          // Force form validation to update
          this.$nextTick(() => {
            if (this.$refs.createForm) {
              this.$refs.createForm.validate();
            }
          });
        })
        .catch(error => {
          console.error('Error checking duplicate name:', error);
          this.duplicateNameExists = false;
        })
        .finally(() => {
          this.checkingDuplicate = false;
        });
    },
  },
};
</script>

<style scoped>
/* ── Breadcrumbs ── */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}
.breadcrumbs-container .v-breadcrumbs {
  background: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

/* ── Hero Card ── */
.sb-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 28px;
  border: none !important;
}
.sb-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 32px 36px;
  position: relative;
  overflow: hidden;
}
.sb-hero-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
}
.sb-hero-header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}
.sb-hero-logo {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}
.sb-hero-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.02em;
}
.sb-hero-subtitle {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

/* ── Intro Text ── */
.sb-intro-text {
  font-size: 0.95rem;
  color: #546e7a;
  line-height: 1.7;
}

/* ── Workflow Section ── */
.sb-workflow-section {
  padding: 28px 36px 32px;
}
.sb-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

/* ── Info Cards ── */
.sb-info-card {
  background: #f8f9ff;
  border: 1px solid #e8eaf6;
  border-radius: 14px;
  padding: 24px;
  height: 100%;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.sb-info-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(26, 35, 126, 0.1);
}
.sb-info-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}
.sb-info-icon-wrapper.map-icon {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
}
.sb-info-icon-wrapper.setup-icon {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
}
.sb-info-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #1a237e;
  margin-bottom: 12px;
}
.sb-info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.sb-info-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
  font-size: 0.9rem;
  color: #455a64;
  line-height: 1.5;
}
.sb-info-list li .v-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

/* ── Form Card ── */
.sb-form-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  margin-bottom: 28px;
}
.sb-form-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%);
  padding: 18px 28px;
  display: flex;
  align-items: center;
}

/* ── Config Body ── */
.sb-config-body {
  padding: 28px 32px 24px;
}
.sb-config-step-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.88rem;
  color: #546e7a;
  font-weight: 500;
  margin-bottom: 16px;
}
.sb-config-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: nowrap;
}
.sb-config-select-wrap {
  flex: 1 1 0;
  min-width: 0;
}
.sb-config-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
}
.sb-action-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  height: 40px !important;
  padding: 0 20px !important;
  white-space: nowrap;
}
.sb-action-placeholder {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: 40px;
  border: 1.5px dashed #cfd8dc;
  border-radius: 10px;
  white-space: nowrap;
}
.sb-action-hint {
  font-size: 0.78rem;
  color: #b0bec5;
  font-style: italic;
}
.sb-status-strip {
  display: flex;
  align-items: center;
  margin-top: 14px;
  padding: 8px 14px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eceff1;
}

/* ── Autocomplete Overrides ── */
.v-autocomplete >>> .v-select__selections {
  min-height: 38px;
  padding: 2px 0;
}
.v-autocomplete >>> .v-list-item {
  padding: 0 !important;
  min-height: unset !important;
}
.v-autocomplete >>> .v-list-item__content {
  padding: 0 !important;
}
.v-autocomplete >>> .v-menu__content {
  max-width: 90vw !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
}

/* ── Dropdown item ── */
.sb-dd-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.12s;
}
.sb-dd-item:hover {
  background: #f3f6ff;
}
.sb-dd-item--disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #fafafa;
}
.sb-dd-item--disabled:hover {
  background: #fafafa;
}
.sb-dd-icon {
  flex-shrink: 0;
}
.sb-dd-name {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 500;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.sb-dd-item--disabled .sb-dd-name {
  color: #9e9e9e;
}
.sb-dd-family {
  flex-shrink: 0;
  font-size: 0.68rem !important;
  height: 18px !important;
}
.sb-dd-status {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  color: #9e9e9e;
}
</style>
